import { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../config";
import useTitle from "../hooks/useTitle";

export default function Products() {
  useTitle("Products | Astro Portal");
  const [products, setProducts] = useState([]);
  const [filters, setFilters] = useState({
    search: "",
    category_id: "",
    subcategory_id: "",
    min_price: "",
    max_price: "",
    is_featured: "",
    is_trending: "",
    sort_by: "price_asc",
    per_page: 8,
    page: 1,
  });

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);

  const observer = useRef();

  // Infinite scroll
  const lastProductRef = useCallback(
    (node) => {
      if (loading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          setFilters((prev) => ({ ...prev, page: prev.page + 1 }));
        }
      });
      if (node) observer.current.observe(node);
    },
    [loading, hasMore]
  );

  const getPerPageCount = () => {
    const width = window.innerWidth;
    if (width >= 1200) return 8;
    if (width >= 768) return 4;
    return 2;
  };

  const fetchProducts = async (reset = false) => {
    try {
      setLoading(true);
      const payload = { ...filters };
      Object.keys(payload).forEach(
        (key) => payload[key] === "" && delete payload[key]
      );
      const res = await axios.post(
        `${API_BASE_URL}/products`,
        payload
      );
      const newData = res.data.data || [];
      setProducts((prev) => (reset ? newData : [...prev, ...newData]));
      setHasMore(res.data.current_page < res.data.last_page);
      setTotal(res.data.total);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/categories`);
      setCategories(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchSubcategories = async (category_id) => {
    if (!category_id) return setSubcategories([]);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/categories/${category_id}/subcategories`
      );
      setSubcategories(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "category_id") {
      // When category changes:
      setFilters((prev) => ({
        ...prev,
        category_id: value,
        subcategory_id: "", // reset subcategory
        page: 1,
      }));
      setSubcategories([]); // clear old subcategories from UI
      fetchSubcategories(value);
      setProducts([]); // reset product list
    } else if (name === "subcategory_id") {
      setFilters((prev) => ({
        ...prev,
        subcategory_id: value,
        page: 1,
      }));
      setProducts([]); // reset products
    } else {
      setFilters((prev) => ({
        ...prev,
        [name]: value,
        page: 1,
      }));
      setProducts([]); // reset products
    }
  };

  const handleCheckbox = (name, checked) => {
    setFilters((prev) => ({ ...prev, [name]: checked ? 1 : "", page: 1 }));
    setProducts([]);
  };

  useEffect(() => {
    const updatePerPage = () => {
      setFilters((prev) => ({ ...prev, per_page: getPerPageCount(), page: 1 }));
      setProducts([]);
    };
    updatePerPage();
    window.addEventListener("resize", updatePerPage);
    return () => window.removeEventListener("resize", updatePerPage);
  }, []);

  useEffect(() => {
    fetchProducts(filters.page === 1);
  }, [
    filters.page,
    filters.sort_by,
    filters.is_featured,
    filters.is_trending,
    filters.category_id,
    filters.subcategory_id,
    filters.min_price,
    filters.max_price,
    filters.search,
    filters.per_page,
  ]);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchSubcategories(filters.category_id);
  }, [filters.category_id]);
  const navigate = useNavigate();

  const goToProductPage = (slug) => {
    navigate(`/product/${slug}`);
  };
  const sortOptions = [
    { label: "Price: Low to High", value: "price_asc" },
    { label: "Price: High to Low", value: "price_desc" },
    { label: "Newest First", value: "newest" },
  ];

  return (
    <div className="container my-5">
      <h2 className="fw-bold text-primary mb-4 text-center">Our Products</h2>

      {/* Filters + Search + Sorting in one line */}
      <div className="row mb-4 g-2 align-items-center">
        <div className="col-md-3">
          <input
            type="text"
            name="search"
            className="form-control"
            placeholder="Search products..."
            value={filters.search}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-2">
          <select
            name="category_id"
            className="form-select"
            value={filters.category_id}
            onChange={handleChange}
          >
            <option value="">Category</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.category_name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-2">
          <select
            name="subcategory_id"
            className="form-select"
            value={filters.subcategory_id}
            onChange={handleChange}
            disabled={!subcategories.length}
          >
            <option value="">Subcategory</option>
            {subcategories.map((s) => (
              <option key={s.id} value={s.id}>
                {s.category_name}
              </option>
            ))}
          </select>
        </div>
        <div className="col-md-2 d-flex gap-2">
          <input
            type="number"
            name="min_price"
            className="form-control"
            placeholder="Min"
            value={filters.min_price}
            onChange={handleChange}
          />
          <input
            type="number"
            name="max_price"
            className="form-control"
            placeholder="Max"
            value={filters.max_price}
            onChange={handleChange}
          />
        </div>
        <div className="col-md-2 d-flex gap-2">
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={!!filters.is_featured}
              onChange={(e) => handleCheckbox("is_featured", e.target.checked)}
            />
            <label className="form-check-label small">Featured</label>
          </div>
          <div className="form-check">
            <input
              type="checkbox"
              className="form-check-input"
              checked={!!filters.is_trending}
              onChange={(e) => handleCheckbox("is_trending", e.target.checked)}
            />
            <label className="form-check-label small">Trending</label>
          </div>
        </div>
        <div className="col-md-1">
          <select
            name="sort_by"
            className="form-select"
            value={filters.sort_by}
            onChange={handleChange}
          >
            {sortOptions.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Products Grid */}
      <div className="row g-4">
        {products.map((p, i) => (
          <div
            className="col-6 col-md-4 col-lg-3"
            key={p.slug}
            ref={i === products.length - 1 ? lastProductRef : null}
          >
            <div
              className="card h-100 shadow-lg border-0 rounded overflow-hidden hover-zoom"
              onClick={() => goToProductPage(p.slug)} // make whole card clickable
              style={{ cursor: "pointer" }}
            >
              <div
                className="product-image-wrapper position-relative"
                style={{ height: "250px", overflow: "hidden" }}
              >
                <img
                  src={p.product_image}
                  alt={p.name}
                  className="card-img-top"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    transition: "transform 0.3s",
                  }}
                />
                {p.is_trending && (
                  <span className="badge bg-danger position-absolute top-0 end-0 m-2">
                    Trending
                  </span>
                )}
                {p.is_featured && (
                  <span className="badge bg-success position-absolute top-0 start-0 m-2">
                    Featured
                  </span>
                )}
              </div>
              <div className="card-body d-flex flex-column">
                <h6 className="fw-bold text-truncate">{p.name}</h6>
                <p className="text-muted small mb-2">{p.short_description}</p>
                <div className="mt-auto d-flex justify-content-between align-items-center">
                  <p className="mb-0 fw-bold text-success">
                    ₹{p.sale_price}{" "}
                    <span className="text-decoration-line-through text-secondary small">
                      ₹{p.original_price}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}

        {loading && (
          <div className="text-center py-3">
            <div className="spinner-border text-primary" role="status"></div>
          </div>
        )}

        {!loading && products.length === 0 && (
          <p className="text-center text-muted mt-5">No products found.</p>
        )}
      </div>

      <style>
        {`
          .hover-zoom img:hover {
            transform: scale(1.05);
          }
        `}
      </style>
    </div>
  );
}
